import React, { useState, useEffect, useCallback } from "react";
import { Card, Upload, Spin } from "antd";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import api from "../interceptors/api";
import StandardButton from "../components/StandardButton";
import ProtectedToolWrapper from "../components/ProtectedToolWrapper";

const OcrImage = () => {
  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [ocrText, setOcrText] = useState("");
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const loadImage = useCallback((file) => {
    setImageFile(file);
    setImagePreviewUrl(URL.createObjectURL(file));
    setOcrText("");
  }, []);

  const beforeUpload = (file) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      toast.error(t("upload_only_images"));
      return false;
    }
    loadImage(file);
    return false;
  };

  useEffect(() => {
    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (item.type.startsWith("image/")) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) loadImage(file);
          return;
        }
      }
    };

    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
  }, [loadImage]);

  const handleRunOcr = async () => {
    if (!imageFile) return;
    setLoading(true);
    const toastId = toast.loading(t("extracting"));
    setOcrText("");

    try {
      const formData = new FormData();
      formData.append("image", imageFile);

      const res = await api.post(`/ocr-image`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data.text) {
        setOcrText(res.data.text);
        toast.success(t("text_extracted_success"));
      } else {
        throw new Error(t("ocr_failed"));
      }
    } catch (err) {
      if (err?.response?.data?.error?.includes("Insufficient credits")) {
        toast.error(t("insufficient_credits_error"));
      } else {
        toast.error(t("ocr_failed"));
      }
    } finally {
      toast.dismiss(toastId);
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(ocrText);
      toast.success(t("copy_success"));
    } catch {
      toast.error(t("copy_failed"));
    }
  };

  const handleReset = () => {
    setImageFile(null);
    setImagePreviewUrl(null);
    setOcrText("");
  };

  return (
    <ProtectedToolWrapper>
      <div className="max-w-5xl mx-auto">
        <Card className="p-6 rounded-xl shadow-lg space-y-6">
          {!imageFile && (
            <div className="min-h-[70vh] flex flex-col items-center justify-center gap-4">
              <Upload
                beforeUpload={beforeUpload}
                showUploadList={false}
                accept="image/*"
              >
                <StandardButton
                  size="large"
                  className="px-6 py-3 text-lg"
                  type="secondary"
                >
                  {t("upload_image_button")}
                </StandardButton>
              </Upload>
              <p className="text-gray-400 text-sm">{t("or_paste_from_clipboard")}</p>
            </div>
          )}

          {imagePreviewUrl && (
            <>
              <img
                src={imagePreviewUrl}
                alt="Preview"
                className="w-full max-h-[600px] object-contain border rounded shadow"
              />
              <div className="mt-4 flex flex-col md:flex-row md:items-center gap-4">
                <StandardButton
                  onClick={handleRunOcr}
                  className="w-full md:w-auto"
                  type="secondary"
                >
                  {t("extract_text_button")}
                </StandardButton>
                <StandardButton
                  onClick={handleReset}
                  className="w-full md:w-auto"
                  type="secondary"
                  variant="outlined"
                >
                  {t("upload_different_image")}
                </StandardButton>
              </div>
            </>
          )}

          {ocrText && (
            <div className="mt-6 bg-white p-4 border rounded shadow whitespace-pre-wrap relative">
              <h3 className="font-semibold mb-2">{t("extracted_text_label")}</h3>
              <p>{ocrText}</p>
              <div className="mt-4 flex gap-3">
                <StandardButton
                  onClick={handleCopy}
                  type="secondary"
                  variant="outlined"
                  size="small"
                >
                  {t("copy_button")}
                </StandardButton>
              </div>
            </div>
          )}

          {loading && (
            <div className="text-center">
              <Spin tip={t("processing")} />
            </div>
          )}
        </Card>
      </div>
    </ProtectedToolWrapper>
  );
};

export default OcrImage;
